import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import isEqual from 'lodash/isEqual';
import { getAppInstance, setAppProperty } from '@webapp-next/lib/app';
import { WebappAppJwtCacheToken } from '@/types/app';

// JWT --------------------------------------------------
const SKEW_MS = 30 * 1000;

const app = getAppInstance();
setAppProperty(
  'JWT_CACHE',
  app.JWT_CACHE || {
    expiringTime: 3, // minutes — fallback if the JWT has no `exp` claim
    tokens: {},
    requestsAwaitingJwt: {}, // cache for pending requests that need JWT token
  }
);

const jwtCache = app.JWT_CACHE;

/**
 * Decode the JWT payload and return its `exp` claim in milliseconds, or null
 * if the token isn't a parseable JWT or has no `exp`.
 */
function getJwtExpiryMs(token: string): number | null {
  if (typeof token !== 'string') {
    return null;
  }
  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '==='.slice((base64.length + 3) % 4);
    const payload = JSON.parse(atob(padded));
    if (typeof payload.exp !== 'number') {
      return null;
    }
    return payload.exp * 1000;
  } catch {
    return null;
  }
}

function isTokenValid(
  aud: string,
  claims: WebappAppJwtCacheToken['claims']
): boolean {
  const token = jwtCache.tokens[aud];

  if (!token) {
    return false;
  }

  // Treat the token as expired SKEW_MS before its real expiry so a request
  // fired right at the edge doesn't reach the server with a dead token.
  const expiresMs = new Date(token.expires).getTime();
  const isNotExpired = expiresMs - SKEW_MS >= Date.now();
  const isClaimsEqual = isEqual(token?.claims, claims);

  return !!(isNotExpired && isClaimsEqual);
}

function waitForToken(
  aud: string
): Promise<WebappAppJwtCacheToken['token'] | null> {
  return new Promise((resolve, reject) => {
    if (!jwtCache.requestsAwaitingJwt[aud]) {
      jwtCache.requestsAwaitingJwt[aud] = [];
    }

    jwtCache.requestsAwaitingJwt[aud].push({ resolve, reject });
  });
}

function continueRequestsAwaitingJwt(
  aud: string,
  token: string | null,
  success = true
): void {
  const requestsAwaitingJwt = jwtCache.requestsAwaitingJwt[aud];
  if (!requestsAwaitingJwt) {
    return;
  }

  if (success) {
    requestsAwaitingJwt.forEach(({ resolve }) => resolve(token));
  } else {
    requestsAwaitingJwt.forEach(({ reject }) => reject(token));
  }

  // clear promises cache of this audience to prevent memory leak
  delete jwtCache.requestsAwaitingJwt[aud];
}

export function setJwtCache(
  aud: string,
  claims: WebappAppJwtCacheToken['claims'],
  token: string
): void {
  const expMs = getJwtExpiryMs(token);
  jwtCache.tokens[aud] = {
    claims,
    token,
    expires:
      expMs != null
        ? new Date(expMs)
        : new Date(Date.now() + jwtCache.expiringTime * 60000),
  };
}

export function clearJwtCache(aud?: string): void {
  if (!aud) {
    jwtCache.tokens = {};
    jwtCache.requestsAwaitingJwt = {};
  } else {
    delete jwtCache.tokens[aud];
    continueRequestsAwaitingJwt(aud, null, false);
  }
}

/**
 * @typedef JWTClaim
 *  @property {String} deputy_user_id
 *  @property {String} deputy_business_id
 *  @property {String} deputy_employee_profile_id
 *  @property {String} deputy_business_user_id
 *  @property {String} deputy_business_location_id
 */

/**
 * @typedef JWT
 *  @property {String} [aud='']
 *  @property {JWTClaim} claims
 *  @property {String} [baseUrl='/api/v1']
 *  @property {String} [url=null]
 */

// baseUrl must stay root-relative — nothing sets axios.defaults.baseURL, so a
// relative default resolves against the page path and misses the API.
export default async function jwt({
  aud,
  claims,
  baseUrl = '/api/v1',
  url = null,
}: {
  aud: string;
  claims: WebappAppJwtCacheToken['claims'];
  baseUrl?: string;
  url?: string | null;
}): Promise<WebappAppJwtCacheToken['token'] | boolean | null> {
  if (!aud) {
    throw new Error('aud is required');
  }
  if (!claims) {
    throw new Error('claims is required');
  }

  // Check if cached token is valid or the requested token is a new one
  if (isTokenValid(aud, claims)) {
    return jwtCache.tokens[aud].token;
  }

  // If there has been pending request of the same audience, wait for it
  if (jwtCache.requestsAwaitingJwt[aud]) {
    return await waitForToken(aud);
  }

  try {
    jwtCache.requestsAwaitingJwt[aud] = [];

    const { data, success, errors } = (
      await axios.post<
        { aud: string; claims: WebappAppJwtCacheToken['claims'] },
        {
          data: {
            data: { token: WebappAppJwtCacheToken['token'] };
            success: boolean;
            errors: string;
          };
        }
      >(url ? url : `${baseUrl}/auth/jwt`, {
        aud,
        claims,
      })
    ).data;

    if (!success) {
      console.error('jwt request failed:', errors);
      continueRequestsAwaitingJwt(aud, null, false);
      return false;
    }

    setJwtCache(aud, claims, data.token);

    // Release pending token requests of the same audience
    continueRequestsAwaitingJwt(aud, data.token);

    return jwtCache.tokens[aud].token;
  } catch (errors) {
    console.log('jwt request error:', errors);
    continueRequestsAwaitingJwt(aud, null, false);
  }

  return false;
}

function install401RetryInterceptor(
  axiosInstance: AxiosInstance,
  resolveAud: (config: AxiosRequestConfig) => string | undefined,
  header: string
): void {
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error?.config as
        | (AxiosRequestConfig & { _retry?: boolean })
        | undefined;

      if (error?.response?.status !== 401 || !config || config._retry) {
        return Promise.reject(error);
      }

      // Only retry safe methods — replaying a mutation after a stale 401
      // risks duplicate side effects across consuming MFEs.
      const method = (config.method ?? 'get').toLowerCase();
      if (method !== 'get' && method !== 'head' && method !== 'options') {
        return Promise.reject(error);
      }

      const aud = resolveAud(config);
      if (!aud) {
        return Promise.reject(error);
      }

      config._retry = true;
      // Drop the stale header so the retry can't resend it if jwt() fails
      // to refresh — the request interceptor will set a fresh one when it runs.
      if (config.headers) {
        delete (config.headers as Record<string, unknown>)[header];
      }
      clearJwtCache(aud);
      return axiosInstance.request(config);
    }
  );
}

export function applyJwtInterceptor(
  axiosInstance: AxiosInstance,
  {
    aud,
    claims,
    baseUrl,
    header = 'Authorization',
    headerPrefix = 'Bearer',
  }: {
    aud: string;
    claims: WebappAppJwtCacheToken['claims'];
    baseUrl?: string;
    header?: string;
    headerPrefix?: string;
  }
): void {
  axiosInstance.interceptors.request.use(async function(config) {
    const token = await jwt({ aud, claims, baseUrl });

    if (token && config.headers) {
      config.headers[header] = `${headerPrefix} ${token}`;
    }

    return config;
  });

  install401RetryInterceptor(axiosInstance, () => aud, header);
}

export function applyDynamicJwtInterceptor(
  axiosInstance: AxiosInstance,
  {
    resolveAudience,
    claims = {},
    baseUrl,
    header = 'Authorization',
    headerPrefix = 'Bearer',
  }: {
    resolveAudience: (url: string) => string | undefined;
    claims?: WebappAppJwtCacheToken['claims'];
    baseUrl?: string;
    header?: string;
    headerPrefix?: string;
  }
): void {
  axiosInstance.interceptors.request.use(async function(config) {
    const url = config.url ?? '';
    const aud = resolveAudience(url);

    if (!aud) {
      return config;
    }

    const token = await jwt({ aud, claims, baseUrl });

    if (token && config.headers) {
      config.headers[header] = `${headerPrefix} ${token}`;
    }

    return config;
  });

  install401RetryInterceptor(
    axiosInstance,
    (config) => resolveAudience(config.url ?? ''),
    header
  );
}
